import { faFileSlash, faUserGraduate } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { usePostHog } from 'posthog-js/react'
import { useCallback, useMemo, useState } from 'react'
import { Form, Submit } from '~/components/Form'
import { SubmitAlignment } from '~/components/Form/SubmitAlignment'
import { content as contentModalStyle } from '~/components/Global/Design/Modal/Modal.css'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import {
  applicantModalAside,
  applicantModalAsideSection,
  applicantModalAsideSectionHighlighted,
  applicantModalAsideSectionTitle,
  applicantModalMain,
  applicantModalMainHeader,
  applicantModalWrapper
} from '~/components/Scopes/Applicants/ApplicantModal/ApplicantModal.css'
import { ApplicantModalLayout } from '~/domains/applicants/components/ApplicantModal/ApplicantModalLayout'
import { AttachmentsViewer } from '~/domains/attachments/components/AttachmentsViewer'
import { useConfig } from '~/domains/configuration/queries/config'
import { useOffer } from '~/domains/offers/queries/useOffer'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { isHtmlEmpty, toast } from '~/utils'
import { captureBulkProcessSubmitted } from '../analytics'
import { bulkProcessActionDescriptor } from '../bulkProcessActions'
import {
  type BulkProcessAction,
  useBulkProcessApplications
} from '../mutations/bulkProcessApplications'
import { useApplication } from '../queries/useApplication'
import { useApplicationsHead } from '../queries/useApplicationsHead'
import {
  bulkAttachmentApplicationId,
  combineBulkAttachments
} from '../utils/combineBulkAttachments'
import { countDistinctRecipients } from '../utils/countDistinctRecipients'
import { actionsContainer } from './ApplicationActions.css'
import { bulkModalTitle, bulkModalTitleCount } from './BulkApplicationModal.css'
import { BulkProcessFields } from './BulkProcessFields'
import {
  type BulkProcessFormValues,
  bulkProcessSchema
} from './bulkProcessSchema'

type BulkApplicationModalProps = {
  applicationIds: number[]
  action: BulkProcessAction
  orderId?: number
  onClose: () => void
  onProcessed: () => void
}

export const BulkApplicationModal = ({
  applicationIds,
  action,
  orderId,
  onClose,
  onProcessed
}: BulkApplicationModalProps) => {
  const posthog = usePostHog()
  const actionDescriptor = bulkProcessActionDescriptor(action)

  const { applications, isLoading } = useApplicationsHead(applicationIds)

  const [selectedApplicationId, setSelectedApplicationId] = useState(
    applicationIds[0]
  )
  const {
    data: selectedApplicationDetail,
    isLoading: isSelectedDetailLoading
  } = useApplication({ id: selectedApplicationId })

  const handleAttachmentChange = useCallback((attachmentId: number) => {
    setSelectedApplicationId(bulkAttachmentApplicationId(attachmentId))
  }, [])

  const { data: orderStatuses } = useTaxonomy('applicant_order_statuses')
  const { data: limitSoft } = useConfig('application.limit_soft')
  const { data: limitHard } = useConfig('application.limit_hard')

  const bulkProcessMutation = useBulkProcessApplications({ orderId })

  const attachments = useMemo(
    () =>
      combineBulkAttachments(applications, selectedApplicationDetail, {
        id: selectedApplicationId,
        isLoading: isSelectedDetailLoading
      }),
    [
      applications,
      selectedApplicationDetail,
      selectedApplicationId,
      isSelectedDetailLoading
    ]
  )

  const recipients = useMemo(
    () => countDistinctRecipients(applications),
    [applications]
  )

  // Only worth saying when the selection really holds several applications of a same candidate
  const distinctRecipientCount =
    !isLoading && recipients.distinct < recipients.addressed
      ? recipients.distinct
      : undefined

  const sharedOfferId = useMemo(() => {
    const firstOfferId = applications[0]?.offer?.id
    if (firstOfferId === undefined) {
      return undefined
    }
    return applications.every(
      (application) => application.offer?.id === firstOfferId
    )
      ? firstOfferId
      : undefined
  }, [applications])

  const { data: sharedOffer } = useOffer(sharedOfferId)

  const filteredOrderStatuses =
    orderStatuses?.filter((status) => status.specialStatus !== 'accepted') || []

  const defaultOrderStatus = filteredOrderStatuses.find(
    (status) => status.specialStatus === 'selected'
  )

  const softLimitReached =
    limitSoft !== undefined && applicationIds.length > limitSoft
  const hardLimitReached =
    limitHard !== undefined &&
    limitHard > 0 &&
    applicationIds.length > limitHard

  const handleSubmit = (data: BulkProcessFormValues) => {
    captureBulkProcessSubmitted(posthog, data.action, applicationIds.length)

    const isOrderAssociation = data.action === 'integrate_accept'

    bulkProcessMutation.mutate(
      {
        applicationIds,
        action: data.action,
        applicantOrderStatus:
          isOrderAssociation && data.orderStatus
            ? Number(data.orderStatus)
            : undefined,
        note:
          isOrderAssociation && !isHtmlEmpty(data.note) ? data.note : undefined,
        sendEmail: data.sendEmail,
        templateId: data.sendEmail ? data.templateId : undefined,
        emailSubject: data.sendEmail ? data.emailSubject : undefined,
        emailContent: data.sendEmail ? data.emailContent : undefined
      },
      {
        onSuccess: (result) => {
          toast(
            'Traitement groupé lancé',
            result.skippedIds.length > 0
              ? `${result.dispatchedCount} candidature${result.dispatchedCount > 1 ? 's' : ''} en cours de traitement, ${result.skippedIds.length} ignorée${result.skippedIds.length > 1 ? 's' : ''}`
              : `${result.dispatchedCount} candidature${result.dispatchedCount > 1 ? 's' : ''} en cours de traitement`,
            ToastType.Success
          )
          onProcessed()
        },
        onError: () => {
          toast(
            'Erreur lors du traitement groupé',
            'Une erreur est survenue lors du lancement du traitement groupé',
            ToastType.Danger
          )
        }
      }
    )
  }

  return (
    <ApplicantModalLayout onClose={onClose}>
      <section className={clsx(contentModalStyle, applicantModalWrapper)}>
        <main className={applicantModalMain}>
          <div className={applicantModalMainHeader}>
            <div className={bulkModalTitle}>
              <FontAwesomeIcon icon={actionDescriptor.icon} />
              {actionDescriptor.label}{' '}
              <span className={bulkModalTitleCount}>
                ({applicationIds.length} candidature
                {applicationIds.length > 1 ? 's' : ''})
              </span>
            </div>
          </div>
          {isLoading && <Shimmer height="100%" />}
          {!isLoading && attachments.length === 0 && (
            <NoResult
              icon={faFileSlash}
              message="Aucun fichier joint à afficher"
            />
          )}
          {!isLoading && attachments.length > 0 && (
            <AttachmentsViewer
              attachments={attachments}
              groupByType
              isLoading={isLoading}
              defaultAttachmentId={attachments[0]?.id}
              onAttachmentChange={handleAttachmentChange}
            />
          )}
        </main>
        <aside className={applicantModalAside}>
          <div
            className={clsx(
              applicantModalAsideSection,
              applicantModalAsideSectionHighlighted
            )}
          >
            <div className={applicantModalAsideSectionTitle}>
              <FontAwesomeIcon icon={faUserGraduate} /> Traitement des
              candidatures
            </div>
            <Alert
              variation={AlertVariation.Info}
              message={actionDescriptor.description}
              onDismiss={false}
            />
            <div className={actionsContainer}>
              {softLimitReached && !hardLimitReached && (
                <Alert
                  variation={AlertVariation.Warning}
                  message={`Vous vous apprêtez à traiter plus de ${limitSoft} candidatures simultanément.`}
                  onDismiss={false}
                />
              )}
              {hardLimitReached && (
                <Alert
                  variation={AlertVariation.Danger}
                  message={`Impossible de traiter plus de ${limitHard} candidatures à la fois.`}
                  onDismiss={false}
                />
              )}
              <Form<BulkProcessFormValues>
                key={`bulk-form-${action}-${sharedOffer?.id ?? 'none'}`}
                schema={bulkProcessSchema}
                defaultValues={{
                  action,
                  orderStatus: defaultOrderStatus
                    ? String(defaultOrderStatus.id)
                    : undefined,
                  note: '',
                  sendEmail: false
                }}
                onSubmit={handleSubmit}
              >
                <BulkProcessFields
                  orderStatuses={filteredOrderStatuses}
                  applicationCount={applicationIds.length}
                  distinctRecipientCount={distinctRecipientCount}
                />
                <Submit
                  submitAlignment={SubmitAlignment.Right}
                  disabled={hardLimitReached || bulkProcessMutation.isPending}
                  loadingText="Patientez..."
                  successText="Traitement en cours"
                >
                  {actionDescriptor.label} {applicationIds.length} candidature
                  {applicationIds.length > 1 ? 's' : ''}
                </Submit>
              </Form>
            </div>
          </div>
        </aside>
      </section>
    </ApplicantModalLayout>
  )
}
